import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";

// Riusa esattamente la logica cromatica del grafico "Andamento timbrature" esistente (OrbitChart).
function colorFor(pct) {
  if (pct > 90) return "#16A34A";   // verde
  if (pct >= 80) return "#CA8A04";  // giallo
  if (pct >= 75) return "#EA580C";  // arancione
  return "#DC2626";                 // rosso
}
const fmt1 = (n) => n.toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const MONTHS = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];

// Anello identico a OrbitChart (rappresentazione invariata), impaginato in verticale per la simmetria.
function Ring({ data }) {
  const correct = data?.correct || 0;
  const irregular = data?.irregular || 0;
  const total = correct + irregular;
  const pct = total ? (data?.percent_correct ?? 100) : 100;
  const irregularPct = total ? Math.round((irregular / total) * 1000) / 10 : 0;
  const accent = colorFor(pct);

  const size = 128, stroke = 10, r = (size - stroke) / 2;
  const C = 2 * Math.PI * r;
  const correctLen = (pct / 100) * C;

  return (
    <div className="flex flex-col items-center">
      <div className="relative shrink-0" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#E2E8F0" strokeWidth={stroke} />
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#CBD5E1" strokeWidth={stroke}
            strokeDasharray={`${C} ${C}`} strokeDashoffset={0} strokeLinecap="round" />
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={accent} strokeWidth={stroke}
            strokeDasharray={`${correctLen} ${C}`} strokeDashoffset={0} strokeLinecap="round"
            style={{ transition: "stroke-dasharray 0.6s ease, stroke 0.4s ease" }} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-heading text-2xl font-bold leading-none" style={{ color: accent }}>{fmt1(pct)}%</span>
          <span className="text-[11px] text-slate-500 mt-0.5">corrette</span>
        </div>
      </div>
      <p className="text-xs text-slate-500 mt-3 text-center">
        <span style={{ color: accent }} className="font-semibold">Corrette {fmt1(pct)}%</span>
        <span className="text-slate-400"> · </span>
        <span className="text-slate-500 font-semibold">Irregolari {fmt1(irregularPct)}%</span>
      </p>
      {total > 0 ? (
        <p className="text-[11px] text-slate-400 mt-1 text-center">{correct} corrette · {irregular} irregolari su {total} timbrature</p>
      ) : (
        <p className="text-[11px] text-slate-400 mt-1 text-center">Nessuna timbratura nel periodo</p>
      )}
    </div>
  );
}

function NavArrows({ label, onPrev, onNext, nextDisabled, testid }) {
  return (
    <div className="flex items-center justify-center gap-2 mb-4">
      <button data-testid={`${testid}-prev`} onClick={onPrev}
        className="h-8 w-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50 transition-colors">
        <ChevronLeft className="h-4 w-4" />
      </button>
      <span data-testid={`${testid}-label`} className="text-sm font-semibold text-slate-700 capitalize text-center min-w-[8rem]">{label}</span>
      <button data-testid={`${testid}-next`} onClick={onNext} disabled={nextDisabled}
        className="h-8 w-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent">
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
}

export function StampTrend() {
  const now = new Date();
  const curYear = now.getFullYear();
  const curMonth = now.getMonth() + 1; // 1-12

  const [my, setMy] = useState(curYear);    // anno del grafico mensile
  const [mm, setMm] = useState(curMonth);   // mese del grafico mensile
  const [ay, setAy] = useState(curYear);    // anno del grafico annuale

  const [monthData, setMonthData] = useState(null);
  const [yearData, setYearData] = useState(null);

  useEffect(() => {
    setMonthData(null);
    api.get(`/my/anomaly-stats?year=${my}&month=${mm}`).then((r) => setMonthData(r.data)).catch(() => setMonthData(null));
  }, [my, mm]);

  useEffect(() => {
    setYearData(null);
    api.get(`/my/anomaly-stats?year=${ay}`).then((r) => setYearData(r.data)).catch(() => setYearData(null));
  }, [ay]);

  const prevMonth = () => {
    if (mm === 1) { setMm(12); setMy((y) => y - 1); } else { setMm((m) => m - 1); }
  };
  const nextMonth = () => {
    if (mm === 12) { setMm(1); setMy((y) => y + 1); } else { setMm((m) => m + 1); }
  };
  const monthAtCurrent = my === curYear && mm === curMonth;
  const yearAtCurrent = ay === curYear;

  return (
    <div data-testid="stamp-trend" className="orbit-area-title mb-4">
      {/* Titolo di sezione FUORI dal box (stesso pattern di Timeline settimanale) */}
      <h2 className="font-heading text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
        <span className="inline-block h-4 w-1.5 rounded-full bg-[#C6A3B4]" />Andamento timbrature
      </h2>

      <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-4">
        {/* Piccola icona interna coerente con la palette Lavoratore */}
        <div className="flex items-center gap-2 mb-4">
          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-[#D6E7F3] to-[#A9C7DD] border border-[#93B4CC] flex items-center justify-center shrink-0">
            <TrendingUp className="h-4 w-4 text-[#22364D]" />
          </div>
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Regolarità delle timbrature</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:divide-x md:divide-slate-100">
          {/* Mensile */}
          <div className="md:pr-6" data-testid="stamp-trend-monthly">
            <p className="text-center text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Mensile</p>
            <NavArrows testid="trend-month" label={`${MONTHS[mm - 1]} ${my}`}
              onPrev={prevMonth} onNext={nextMonth} nextDisabled={monthAtCurrent} />
            {monthData ? <Ring data={monthData} /> : <p className="text-sm text-slate-400 text-center py-12">Caricamento…</p>}
          </div>

          {/* Annuale */}
          <div className="md:pl-6" data-testid="stamp-trend-annual">
            <p className="text-center text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Annuale</p>
            <NavArrows testid="trend-year" label={`${ay}`}
              onPrev={() => setAy((y) => y - 1)} onNext={() => setAy((y) => y + 1)} nextDisabled={yearAtCurrent} />
            {yearData ? <Ring data={yearData} /> : <p className="text-sm text-slate-400 text-center py-12">Caricamento…</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
